import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { KizenTypography } from '__app/typography';
import { Button } from '@kizen/kds/Button';
import { Gradient } from '__components/Kizen/ScrollFadeContainer';
import {
  StyledButtonsWrapper,
  StyledPageWrapper,
  StyledPaginationWrapper,
  StyledSearchInput,
  StyledTableScrollContainer,
  StyledTableToolbar,
  StyledTableWrapper,
} from 'pages/KnowledgeSettings/styles';
import BasicTable from '__components/Tables/Basic';
import { TRow } from '__components/Kizen/Table';
import TablePagination from 'components/Tables/Big/TablePagination';
import {
  CATALOGS_TABLE_ACTIONS,
  DEFAULT_CATALOGS_PAGINATION_SETTINGS,
} from 'pages/KnowledgeSettings/constants';
import { useColumns } from './columns';
import { knowledgeCatalogListQuery } from 'query/knowledge';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import KnowledgeService from 'services/KnowledgeService';
import type { KnowledgeCatalog, KnowledgeCatalogListParams } from '@kizen/api/knowledge';
import { useToast } from '__components/ToastProvider';
import { getOriginalError } from '__services/AxiosService';
import { getErrorMessage } from '__hooks/useErrors';
import useCan from 'hooks/useCan';
import { usePaginationSettings } from 'hooks/usePaginationSettings';
import Input from '__components/Kizen/Input';
import { NewCatalogModal } from './NewCatalogModal';
import { DeleteCatalogModal } from './DeleteCatalogModal';

export const Catalogs = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [showToast, toastVariant] = useToast();
  const queryClient = useQueryClient();

  const canWrite = useCan({
    edit: 'sections',
    for: 'settings_section.knowledgebase_setting',
  });

  const canViewSection = useCan({
    view: 'sections',
    for: 'settings_section.knowledgebase_setting',
  });

  const {
    page,
    pageSize,
    search,
    ordering,
    actualSearch,
    handleChangePage,
    handleChangePageSize,
    handleChangeSearch,
    headData,
  } = usePaginationSettings(DEFAULT_CATALOGS_PAGINATION_SETTINGS);

  const [showNewCatalogModal, setShowNewCatalogModal] = useState(false);
  const [catalogToDelete, setCatalogToDelete] =
    useState<KnowledgeCatalog | null>(null);

  const queryOptions = useMemo(
    () =>
      knowledgeCatalogListQuery({
        params: { page, page_size: pageSize, search, ordering: ordering as KnowledgeCatalogListParams['ordering'] },
        placeholderData: keepPreviousData,
        staleTime: 0,
      }),
    [page, pageSize, search, ordering]
  );

  const { data: rawData, isLoading, isFetching } = useQuery(queryOptions);
  const loading = isLoading || isFetching;

  // Business Knowledge catalog always appears first
  const data = useMemo(() => {
    if (!rawData) return rawData;
    const sorted = [...rawData.results].sort((a, b) => {
      if (a.is_default && !b.is_default) return -1;
      if (!a.is_default && b.is_default) return 1;
      return 0;
    });
    return { ...rawData, results: sorted };
  }, [rawData]);

  const onRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
  }, [queryClient, queryOptions.queryKey]);

  const navigateToCatalog = useCallback(
    (catalogId: string, subpage?: string) => {
      const path = subpage
        ? `/knowledge/${catalogId}/${subpage}`
        : `/knowledge/${catalogId}`;
      history.push(path);
    },
    [history]
  );

  const { mutateAsync: toggleCatalog } = useMutation({
    mutationFn: ({ id, is_enabled }: { id: string; is_enabled: boolean }) =>
      KnowledgeService.updateKnowledgeCatalog(id, { is_enabled }),
    onSuccess: () => onRefresh(),
  });

  const { mutateAsync: deleteCatalogMutation } = useMutation({
    mutationFn: (id: string) => KnowledgeService.deleteKnowledgeCatalog(id),
    onSuccess: () => onRefresh(),
  });

  const onSelectAction = useCallback(
    async (
      { value }: { value: keyof typeof CATALOGS_TABLE_ACTIONS },
      catalog: KnowledgeCatalog
    ) => {
      if (value === CATALOGS_TABLE_ACTIONS.view) {
        navigateToCatalog(catalog.id);
      } else if (value === CATALOGS_TABLE_ACTIONS.enable) {
        try {
          await toggleCatalog({ id: catalog.id, is_enabled: true });
          showToast({
            message: t('Catalog enabled'),
            variant: toastVariant.SUCCESS,
          });
        } catch (error) {
          showToast({
            message:
              getErrorMessage(getOriginalError(error)) ||
              t('Failed to enable catalog'),
            variant: toastVariant.FAILURE,
          });
        }
      } else if (value === CATALOGS_TABLE_ACTIONS.disable) {
        try {
          await toggleCatalog({ id: catalog.id, is_enabled: false });
          showToast({
            message: t('Catalog disabled'),
            variant: toastVariant.SUCCESS,
          });
        } catch (error) {
          showToast({
            message:
              getErrorMessage(getOriginalError(error)) ||
              t('Failed to disable catalog'),
            variant: toastVariant.FAILURE,
          });
        }
      } else if (value === CATALOGS_TABLE_ACTIONS.delete) {
        setCatalogToDelete(catalog);
      }
    },
    [navigateToCatalog, toggleCatalog, showToast, t, toastVariant]
  );

  const handleDeleteConfirmed = useCallback(async () => {
    if (!catalogToDelete) return;
    try {
      await deleteCatalogMutation(catalogToDelete.id);
      showToast({
        message: t('Catalog deleted successfully'),
        variant: toastVariant.SUCCESS,
      });
      setCatalogToDelete(null);
    } catch (error) {
      showToast({
        message:
          getErrorMessage(getOriginalError(error)) ||
          t('Failed to delete catalog'),
        variant: toastVariant.FAILURE,
      });
    }
  }, [catalogToDelete, deleteCatalogMutation, showToast, t, toastVariant]);

  const handleCreateCatalog = useCallback(
    async (name: string, description: string, sharingSettings: Record<string, unknown>, isPublishable: boolean) => {
      try {
        const created = await KnowledgeService.createKnowledgeCatalog({
          name,
          description: description || undefined,
          sharing_settings: sharingSettings as any,
          is_publishable: isPublishable,
        });
        showToast({
          message: t('Catalog created successfully'),
          variant: toastVariant.SUCCESS,
        });
        setShowNewCatalogModal(false);
        navigateToCatalog(created.id);
      } catch (error) {
        showToast({
          message:
            getErrorMessage(getOriginalError(error)) ||
            t('Failed to create catalog'),
          variant: toastVariant.FAILURE,
        });
      }
    },
    [navigateToCatalog, showToast, t, toastVariant]
  );

  const columns = useColumns({
    onSelectAction,
    onClickName: (catalog) => navigateToCatalog(catalog.id),
    onClickFileSources: (catalog) =>
      navigateToCatalog(catalog.id, 'file-sources'),
    onClickOnlineSources: (catalog) =>
      navigateToCatalog(catalog.id, 'online-sources'),
    onClickSharingSettings: (catalog) =>
      navigateToCatalog(catalog.id, 'team-sharing'),
    canWrite,
    canViewSection,
  });

  return (
    <StyledPageWrapper>
      <StyledTableToolbar>
        <StyledSearchInput
          pill
          type={Input.INPUT_TYPES.SEARCH}
          placeholder={t('Find Catalogs')}
          value={actualSearch}
          onChange={handleChangeSearch}
          data-qa-id="catalogs-search-input"
        />
        <KizenTypography as="h2" type="header">
          {t('{{count}} Knowledge {{instance}}', {
            count: data?.count ?? 0,
            instance: data?.count === 1 ? t('Catalog') : t('Catalogs'),
          })}
        </KizenTypography>
        <StyledButtonsWrapper>
          {canWrite ? (
            <Button
              onClick={() => setShowNewCatalogModal(true)}
              data-qa-id="new-catalog-button"
            >
              {t('New Catalog')}
            </Button>
          ) : null}
        </StyledButtonsWrapper>
      </StyledTableToolbar>
      <StyledTableWrapper>
        <StyledTableScrollContainer bottom top gradient={<Gradient />}>
          <BasicTable
            stickyHeader
            head={<TRow head columns={columns} data={headData} />}
            staleData={loading}
          >
            {data?.count ? null : <TRow />}
            {data?.results.map((catalog: KnowledgeCatalog) => (
              <TRow key={catalog.id} columns={columns} data={catalog} />
            ))}
          </BasicTable>
        </StyledTableScrollContainer>
      </StyledTableWrapper>
      <StyledPaginationWrapper>
        <TablePagination
          page={page}
          perPage={pageSize}
          totalCount={data?.count}
          onChangePage={handleChangePage}
          onChangePerPage={handleChangePageSize}
        />
      </StyledPaginationWrapper>

      {showNewCatalogModal ? (
        <NewCatalogModal
          onConfirm={handleCreateCatalog}
          onHide={() => setShowNewCatalogModal(false)}
        />
      ) : null}

      {catalogToDelete ? (
        <DeleteCatalogModal
          catalog={catalogToDelete}
          onConfirm={handleDeleteConfirmed}
          onHide={() => setCatalogToDelete(null)}
        />
      ) : null}
    </StyledPageWrapper>
  );
};
